import React from "react";

import { css, cx } from "@emotion/css";
import { createColumnHelper, getCoreRowModel, useReactTable } from "@tanstack/react-table";

import { useStyles } from "@/hooks/useStyles.ts";
import type { GroupedInclusionDraft } from "@/models/GroupedInclusionModel.ts";
import type { Theme } from "@/theme/CreateTheme.ts";
import { DeleteCell, DeleteHeader } from "@/uiKit/organisms/PatientSync/OngoingInclusion/columns/DeleteColumn.tsx";
import { NameCell, NameHeader } from "@/uiKit/organisms/PatientSync/OngoingInclusion/columns/NameColumn.tsx";
import { PhoneCell, PhoneHeader } from "@/uiKit/organisms/PatientSync/OngoingInclusion/columns/PhoneColumn.tsx";
import { MuiTable } from "@/uiKit/Table.tsx";
import { CellEditionFeature } from "@/utils/table/CellEditionFeature.ts";

export type OngoingInclusionProps = {
  inclusions: GroupedInclusionDraft[];
  className?: string;
};

const columnHelper = createColumnHelper<GroupedInclusionDraft>();

const columnDefinitions = {
  name: columnHelper.accessor("lastName", {
    id: "name",
    cell: NameCell,
    header: NameHeader,
    size: 45,
  }),
  phone: columnHelper.accessor("phoneNumber", {
    id: "phone",
    cell: PhoneCell,
    header: PhoneHeader,
    size: 45,
  }),
  delete: columnHelper.display({
    id: "delete",
    cell: DeleteCell,
    header: DeleteHeader,
    size: 10,
  }),
};

export const OngoingInclusionTable: React.FC<OngoingInclusionProps> = ({ inclusions, className }) => {
  const styles = useStyles(makeStyles);

  const table = useReactTable<GroupedInclusionDraft>({
    _features: [CellEditionFeature],
    columns: Object.values(columnDefinitions),
    data: inclusions,
    defaultColumn: {
      size: 0,
      minSize: 0,
      maxSize: 100,
    },
    getRowId: row => row.id,
    getCoreRowModel: getCoreRowModel(),
    enableRowSelection: false,
  });

  return (
    <div className={cx(styles.container, className)}>
      <MuiTable
        id="ongoing-inclusion-table"
        table={table}
        classes={{ cell: styles.cell, headerCell: styles.headerCell }}
      />
    </div>
  );
};

const makeStyles = (theme: Theme) => ({
  container: css({}),
  cell: css({
    overflow: "clip",
    padding: theme.spacing(4),
  }),
  headerCell: css({
    backgroundColor: theme.palette.grey[200],
    borderBottom: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(4),
  }),
});
